import { XIcon } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface TagInputProps {
  value: readonly string[];
  onChange: (value: string[]) => void;
  onBlur?: (() => void) | undefined;
  placeholder?: string;
  /** Upper bound; the input hides once reached. */
  max?: number;
  /** Per-tag character limit. Longer entries are trimmed. */
  maxLength?: number;
  disabled?: boolean;
  id?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
  /** Accessible name for the remove button; receives the tag. */
  removeLabel?: (tag: string) => string;
  className?: string;
}

const SEPARATORS = /[,\n]/;

/**
 * Chips + a free-text input. Enter, comma or blur commits what was typed; Backspace on an
 * empty input removes the last chip. Duplicates (case-insensitive) are dropped.
 */
export function TagInput({
  value,
  onChange,
  onBlur,
  placeholder,
  max,
  maxLength = 60,
  disabled = false,
  id,
  removeLabel = (tag) => `Remove ${tag}`,
  className,
  ...aria
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const full = max !== undefined && value.length >= max;

  const commit = (raw: string) => {
    const incoming = raw
      .split(SEPARATORS)
      .map((s) => s.trim().slice(0, maxLength))
      .filter(Boolean);
    if (incoming.length === 0) return;
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    for (const tag of incoming) {
      if (max !== undefined && next.length >= max) break;
      const key = tag.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      next.push(tag);
    }
    if (next.length !== value.length) onChange(next);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit(draft);
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      event.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        'border-input focus-within:border-ring focus-within:ring-ring/50 aria-invalid:border-destructive dark:bg-input/30 flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border bg-transparent px-2 py-1 shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      aria-invalid={aria['aria-invalid']}
    >
      {value.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-1 pr-1 font-normal">
          {tag}
          {!disabled ? (
            <button
              type="button"
              className="hover:bg-foreground/10 rounded-full p-0.5"
              onClick={() => onChange(value.filter((v) => v !== tag))}
              aria-label={removeLabel(tag)}
            >
              <XIcon className="size-3" aria-hidden />
            </button>
          ) : null}
        </Badge>
      ))}
      {!full ? (
        <input
          id={id}
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : undefined}
          aria-describedby={aria['aria-describedby']}
          className="placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent py-0.5 text-sm outline-none"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            commit(draft);
            onBlur?.();
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (SEPARATORS.test(text)) {
              e.preventDefault();
              commit(`${draft}${text}`);
            }
          }}
        />
      ) : null}
    </div>
  );
}
